/**
 * Spec 049: «Аккаунты» — what staff members' emulators sign in with. A person stores an account
 * here, its password goes to the secrets store; «Показать», a new password and deleting are the
 * person's who stored it. Marks: «нужен код» and «похоже, забанен» come from the «Агенты» app,
 * «забанен» and «выключен» from people; «занят» — a run signs in with it now.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { Accounts, type AccountApp, type AccountState } from "@/api/accounts";
import { ApiError } from "@/api/client";
import { Agents } from "@/api/resources";
import { Button } from "@/components/ui/Button";
import { TextArea, TextField } from "@/components/ui/Field";
import { Segmented } from "@/components/ui/Segmented";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { ago, Caption, EmptyNote, Pill } from "@/kit";
import { APP_LABEL, heldLine, STATE_LABEL, stateTone } from "@/lib/accounts";

type Filter = "all" | AccountState;
const FILTERS: { value: Filter; label: string }[] = [
  { value: "all", label: "Все" },
  { value: "busy", label: "Заняты" },
  { value: "needs_code", label: "Нужен код" },
  { value: "maybe_banned", label: "Похоже, забанены" },
  { value: "banned", label: "Забанены" },
];
const APPS = Object.keys(APP_LABEL) as AccountApp[];
const SELECT = "min-h-11 rounded-lg border border-p-border-strong bg-p-surface px-3 text-body text-p-text";

/** `agentId`: one staff member's accounts (its card); without it — everyone's («Отделы»). */
export function AccountsBlock({ agentId }: { agentId?: string }) {
  const [filter, setFilter] = useState<Filter>("all");
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const params = { agent_id: agentId, state: filter === "all" ? undefined : filter };
  const list = useQuery({ queryKey: ["accounts", "list", params], queryFn: () => Accounts.list(params) });
  const rows = list.data ?? [];
  return (
    <section className="flex flex-col gap-3" aria-label="Аккаунты">
      <div className="flex items-center justify-between gap-2">
        <Caption>Аккаунты</Caption>
        <Button size="compact" variant="secondary" icon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={() => setAdding(true)}>
          Аккаунт
        </Button>
      </div>
      <p className="text-meta text-p-text-muted">
        Ими входит эмулятор сотрудника. Пароль хранится в секретах таскера: видит и меняет его только тот, кто сохранил, а получает только приложение «Агенты» на его ПК. Агент пароля не видит.
      </p>
      <div className="overflow-x-auto">
        <Segmented options={FILTERS} value={filter} onChange={setFilter} label="Какие аккаунты" />
      </div>
      {list.isLoading && <SkeletonList count={2} />}
      {list.isError && (
        <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void list.refetch()}>Повторить</Button>}>
          Не удалось загрузить аккаунты.
        </EmptyNote>
      )}
      {list.data && !rows.length && <EmptyNote>{filter === "all" ? "Аккаунтов пока нет." : "Таких нет."}</EmptyNote>}
      {rows.length > 0 && (
        <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
          {rows.map((a) => (
            <li key={a.id} className="border-b border-p-border last:border-0">
              <button type="button" className="flex w-full flex-col gap-1 px-4 py-3 text-left press-surface" onClick={() => setOpen(a.id)}>
                <span className="text-[15px] font-semibold leading-5 text-p-text">
                  {APP_LABEL[a.app]} · {a.login}{a.position > 0 ? " · запасной" : ""}
                </span>
                <span className="flex flex-wrap items-center gap-2">
                  <Pill tone={stateTone(a.state)}>{STATE_LABEL[a.state]}</Pill>
                  {!agentId && <span className="text-meta text-p-text-muted">{a.agent_name}</span>}
                  {a.held && <span className="text-meta text-p-text-muted">{heldLine(a.held, a.agent_name)}</span>}
                  {!a.has_password && <span className="text-meta text-p-needs">нет пароля</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {adding && <AddSheet agentId={agentId} onClose={() => setAdding(false)} />}
      {open && <AccountSheet id={open} onClose={() => setOpen(null)} />}
    </section>
  );
}

function AddSheet({ agentId, onClose }: { agentId?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const agents = useQuery({ queryKey: ["agents", "list"], queryFn: () => Agents.list(), enabled: !agentId });
  const [agent, setAgent] = useState(agentId ?? "");
  const [app, setApp] = useState<AccountApp>("instagram");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [note, setNote] = useState("");
  const [spare, setSpare] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      Accounts.create({ agent_id: agent, app, login: login.trim(), password, note: note.trim() || null, position: spare ? 1 : 0 }),
    onSuccess: () => {
      setPassword("");
      void qc.invalidateQueries({ queryKey: ["accounts"] });
      toast.success("Аккаунт сохранён");
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <Sheet open onClose={onClose} title="Новый аккаунт сотрудника"
      footer={<Button block variant="strong" loading={save.isPending} disabled={!agent || !login.trim() || !password} onClick={() => save.mutate()}>Сохранить</Button>}>
      <div className="flex flex-col gap-3">
        {!agentId && (
          <label className="flex flex-col gap-1 text-meta text-p-text-muted">
            Сотрудник
            <select className={SELECT} value={agent} onChange={(e) => setAgent(e.target.value)}>
              <option value="">выбери сотрудника</option>
              {(agents.data ?? []).filter((a) => !a.archived_at).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
        )}
        <label className="flex flex-col gap-1 text-meta text-p-text-muted">
          Приложение
          <select className={SELECT} value={app} onChange={(e) => setApp(e.target.value as AccountApp)}>
            {APPS.map((a) => <option key={a} value={a}>{APP_LABEL[a]}{a === "google" ? " — вход в эмулятор и Google Play" : ""}</option>)}
          </select>
        </label>
        <TextField label="Логин" hint="Почта, телефон или имя пользователя" value={login} maxLength={200} autoComplete="off" onChange={(e) => setLogin(e.target.value)} />
        <TextField label="Пароль" type="password" value={password} maxLength={500} autoComplete="new-password" onChange={(e) => setPassword(e.target.value)} />
        <TextArea label="Заметка" hint="Куда приходят коды: почта, телефон. Не пароль." rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        <label className="flex min-h-11 items-center gap-3">
          <input type="checkbox" className="h-5 w-5 accent-[var(--p-text)]" checked={spare} onChange={() => setSpare(!spare)} />
          <span className="text-body text-p-text">Запасной: входит, когда основной занят или забанен</span>
        </label>
      </div>
    </Sheet>
  );
}

function AccountSheet({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const detail = useQuery({ queryKey: ["accounts", "one", id], queryFn: () => Accounts.get(id) });
  const [why, setWhy] = useState("");
  const [shown, setShown] = useState<string | null>(null);
  const [fresh, setFresh] = useState("");
  const a = detail.data;
  const refresh = () => void qc.invalidateQueries({ queryKey: ["accounts"] });
  const fail = (e: Error) => {
    toast.error(e.message);
    if (e instanceof ApiError && e.status === 409) refresh();
  };
  const update = useMutation({
    mutationFn: (body: Parameters<typeof Accounts.update>[1]) => Accounts.update(id, body),
    onSuccess: () => { setWhy(""); toast.success("Сохранено"); refresh(); },
    onError: fail,
  });
  const reveal = useMutation({ mutationFn: () => Accounts.reveal(id), onSuccess: (r) => setShown(r.password), onError: fail });
  const password = useMutation({
    mutationFn: () => Accounts.password(id, a!.version, fresh),
    onSuccess: () => { setFresh(""); setShown(null); toast.success("Пароль сменён"); refresh(); },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => Accounts.remove(id),
    onSuccess: () => { toast.success("Аккаунт удалён"); refresh(); onClose(); },
    onError: fail,
  });
  return (
    <Sheet open onClose={onClose} title={a ? `${APP_LABEL[a.app]} · ${a.login}` : "Аккаунт"}>
      {detail.isLoading && <SkeletonList count={2} />}
      {detail.isError && <EmptyNote>Не удалось загрузить аккаунт.</EmptyNote>}
      {a && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span className="flex flex-wrap items-center gap-2">
              <Pill tone={stateTone(a.state)}>{STATE_LABEL[a.state]}</Pill>
              <span className="text-meta text-p-text-muted">{a.agent_name}{a.position > 0 ? " · запасной" : ""}</span>
            </span>
            {a.held && <p className="text-meta text-p-text-muted">{heldLine(a.held, a.agent_name)}</p>}
            {a.mark_note && <p className="text-meta text-p-text-muted">Отметка: {a.mark_note}{a.mark_by ? ` · ${a.mark_by}` : ""}{a.mark_at ? ` · ${ago(a.mark_at)}` : ""}</p>}
            {a.note && <p className="whitespace-pre-wrap break-words text-body text-p-text">{a.note}</p>}
          </div>

          <section className="flex flex-col gap-2">
            <Caption>Отметка</Caption>
            <div className="flex flex-wrap gap-2">
              {a.mark !== "ok" && (
                <Button size="compact" variant="secondary" loading={update.isPending} onClick={() => update.mutate({ version: a.version, mark: "ok" })}>
                  {a.mark === "needs_code" ? "Код введён — свободен" : "Снова свободен"}
                </Button>
              )}
              {a.mark !== "disabled" && (
                <Button size="compact" variant="secondary" loading={update.isPending} onClick={() => update.mutate({ version: a.version, mark: "disabled" })}>Выключить</Button>
              )}
              {a.held && (
                <Button size="compact" variant="secondary" loading={update.isPending} onClick={() => update.mutate({ version: a.version, release: true })}>Освободить</Button>
              )}
            </div>
            <TextArea label="Забанен — когда и почему" rows={2} maxLength={1000} value={why} onChange={(e) => setWhy(e.target.value)} />
            <div>
              <Button size="compact" variant="destructive-outline" disabled={!why.trim()} loading={update.isPending}
                onClick={() => update.mutate({ version: a.version, mark: "banned", mark_note: why.trim() })}>Забанен</Button>
            </div>
          </section>

          <section className="flex flex-col gap-2">
            <Caption>Пароль</Caption>
            {!a.has_password && <p className="text-meta text-p-needs">Пароля нет: задай его, иначе эмулятор не войдёт.</p>}
            {a.mine || !a.has_password ? (
              <>
                {a.has_password && (
                  shown !== null ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <code className="select-all break-all rounded-compact bg-p-surface-2 px-2 py-1 text-body text-p-text">{shown}</code>
                      <Button size="compact" variant="ghost" onClick={() => setShown(null)}>Скрыть</Button>
                    </div>
                  ) : (
                    <div><Button size="compact" variant="secondary" loading={reveal.isPending} onClick={() => reveal.mutate()}>Показать</Button></div>
                  )
                )}
                <TextField label={a.has_password ? "Новый пароль" : "Пароль"} type="password" autoComplete="new-password" maxLength={500} value={fresh} onChange={(e) => setFresh(e.target.value)} />
                <div><Button size="compact" variant="secondary" disabled={!fresh} loading={password.isPending} onClick={() => password.mutate()}>Сохранить пароль</Button></div>
              </>
            ) : (
              <p className="text-meta text-p-text-muted">Пароль сохранил {a.created_by}: видит и меняет его он.</p>
            )}
          </section>

          {a.history.length > 0 && (
            <section className="flex flex-col gap-1">
              <Caption>История</Caption>
              {a.history.map((h, i) => (
                <span key={`${h.at}-${i}`} className="text-meta text-p-text-muted">{historyLine(h.type, h.payload)}{h.by ? ` · ${h.by}` : ""} · {ago(h.at)}</span>
              ))}
            </section>
          )}

          <p className="text-micro text-p-text-muted">Сохранил: {a.created_by} · {ago(a.created_at)}</p>
          {a.mine && (
            <button type="button" className="min-h-11 self-start text-meta font-semibold text-p-blocked" disabled={remove.isPending}
              onClick={() => { if (window.confirm("Удалить аккаунт? Пароль сотрётся из хранилища.")) remove.mutate(); }}>
              Удалить
            </button>
          )}
        </div>
      )}
    </Sheet>
  );
}

const EVENT: Record<string, string> = {
  "staff_account.created": "сохранён",
  "staff_account.updated": "изменён",
  "staff_account.password_changed": "новый пароль",
  "staff_account.revealed": "пароль показан",
  "staff_account.claimed": "вход в эмуляторе",
  "staff_account.released": "освобождён",
  "staff_account.marked": "отметка приложения",
  "staff_account.followed": "подписка",
  "staff_account.follow_blocked": "приложение ограничило подписки — пауза 72 ч",
};

function historyLine(type: string, payload: Record<string, unknown>): string {
  const base = EVENT[type] ?? type;
  // Spec 049, step 4: whom the staff member followed, how big the author is and why.
  const { profile, followers, why, note } = payload;
  if (type === "staff_account.followed") {
    const many = typeof followers === "number" ? ` (${followers.toLocaleString("ru-RU")} подписчиков)` : "";
    return `${base}: ${String(profile ?? "")}${many}${typeof why === "string" ? ` — ${why}` : ""}`;
  }
  if (type === "staff_account.follow_blocked" && typeof note === "string") return `${base}: «${note}»`;
  const raw = typeof payload.mark === "string" ? payload.mark : null;
  const mark = raw === "ok" ? STATE_LABEL.free : raw ? STATE_LABEL[raw as AccountState] ?? raw : null;
  return mark ? `${base}: ${mark}` : base;
}
